<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <colgroup>
                <col style="width: 30%;" />
                <col style="width: 10%;" />
                <col style="width: 10%;" />
                <col style="width: 36%;" />
                <col style="width: 10%;" />
                <col style="width: 4%;" />
            </colgroup>
            <b-thead>
                <b-tr>
                    <b-th>Client:</b-th>
                    <b-th>Invoice:</b-th>
                    <b-th class="text-right pr-3">Amount:</b-th>
                    <b-th>Note:</b-th>
                    <b-th>Write Off Date:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="item in writeOff" :key="item.id">
                    <b-td>
                        <span v-if="item.client">
                            <client-name
                                :client="item.client"
                                :text="item.client.display_name"
                            />
                        </span>
                    </b-td>
                    <b-td class="text-nowrap">
                        <a :href="env.APPS + item.invoice.ci_url">
                            {{ item.invoice_name || item.invoice.name }}
                        </a>
                    </b-td>
                    <b-td class="text-right pr-3">{{
                        _toCurrency(item.amount)
                    }}</b-td>
                    <b-td v-html="convertNewLineToBreak(item.note)"></b-td>
                    <b-td class="text-nowrap">
                        {{
                            item.writeoff_date
                                ? _carbonDateDisplay(item.writeoff_date)
                                : item.create_at
                                ? _carbonDateDisplay(item.writeoff_date)
                                : ""
                        }}</b-td
                    >
                    <b-td class="text-right">
                        <b-dropdown
                            v-if="
                                item.is_active_actor &&
                                (_hasAccess('billing.writeoff.update') ||
                                    _hasAccess('billing.writeoff.delete'))
                            "
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            right
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>

                            <b-dropdown-item
                                href="#"
                                v-if="_hasAccess('billing.writeoff.update')"
                                @click="showEditModal(item)"
                            >
                                <i class="cus-page-white-edit"></i>
                                Edit Write Off
                            </b-dropdown-item>
                            <b-dropdown-item
                                href="#"
                                v-if="_hasAccess('billing.writeoff.delete')"
                                @click="showDeleteModal(item)"
                            >
                                <i class="cus-delete"></i>
                                Delete Write Off
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                </b-tr>
                <b-tr v-if="writeOff.length == 0 && !isLoading">
                    <b-td colspan="6"><i>No records available</i></b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta && showPagination"
            v-show="meta.lastPage > 1"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            class="vet-pagination"
            :hide-goto-end-buttons="true"
            @change="getWriteOffAll"
        />
        <modal-edit
            ref="modalEdit"
            modal-ref="modal-save-writeoff"
            :write-off="selectedWriteOff"
            @reload="reloadRelated"
        />
        <modal-delete
            modal-id="modal-delete-writeoff"
            :write-off="selectedWriteOff"
            @reload="reloadRelated"
        />
    </div>
</template>

<script>
/**
 * @name WriteOffList
 * @description List of write off based on invoice
 * @see apps/assets/js/module/common/model/item_form_model.js
 * @see apps/application/views/staff/billing/sub/navbar.php
 * @see apps/application/views/staff/billing/sub/writeoff/create_update.php
 * @see apps/application/views/staff/billing/sub/writeoff/list.php
 * @see apps/assets/js/module/billing/writeoff.js
 * @see apps/application/models/orm/om/BaseTblWriteOff.php
 * @see services/barramundi/app/Models/Invoice.php
 */
import Loader from "@/components/elements/loader-list";
import BillingWriteOffServices from "@/services/billing/billing-writeoff";
import ClientName from "@/components/commons/client-name";
import ModalDelete from "@/components/billing-writeoffs/writeoff-delete-modal";
import ModalEdit from "@/components/billing-writeoffs/writeoff-save-modal";

export default {
    mixins: {},
    components: {
        Loader,
        ClientName,
        ModalDelete,
        ModalEdit
    },
    data() {
        return {
            isLoading: false,
            showPagination: false,
            writeOff: [],
            meta: null,
            selectedWriteOff: null
        };
    },
    mounted() {
        this.getWriteOffAll();
    },
    methods: {
        getWriteOffAll(page = 1) {
            let self = this;
            this.isLoading = true;
            this.writeOff = [];

            BillingWriteOffServices.getWriteOffAll(this.PER_PAGE, page)
                .then(res => {
                    this.isLoading = false;
                    self.meta = self.standardizePagingKey(res);

                    if (self.meta.total > 20) {
                        self.showPagination = true;
                    }

                    if (res.data) {
                        self.writeOff = res.data.map(data =>
                            self.prepareData(data)
                        );
                    }
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to get write off list",
                        "danger"
                    );
                    this.isLoading = false;
                    console.error(error);
                });
        },
        prepareData(data) {
            data.client = {
                display_name: data.client_name,
                name: data.client_name,
                encoded_id: data.client_encoded_id,
                id: data.client_id
            };
            data.invoice = {
                name: data.invoice_name,
                ci_url: "billing/invoice/view/" + data.invoice_encoded_id,
                id: data.invoice_id
            };
            data.amount = this.checkAmount(data.amount);
            return data;
        },
        checkAmount(amount) {
            if (String(amount).includes(this._currency)) {
                amount = Number(amount.replace(this._currency, ""));
            }
            return amount;
        },
        reloadRelated() {
            this.getWriteOffAll();
            // this.$emit("reload-item-details");
        },
        standardizePagingKey(res) {
            res.lastPage = res.last_page;
            res.currentPage = res.current_page;
            res.perPage = res.per_page;
            return res;
        },
        showEditModal(writeOff) {
            this.selectedWriteOff = writeOff;
            let date = this._fromDateCarbon(writeOff.writeoff_date).toFormat(
                "X"
            );

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$refs.modalEdit.open(),
                onError: message => this._message(message, "danger")
            });
        },
        showDeleteModal(writeOff) {
            this.selectedWriteOff = writeOff;
            let date = this._fromDateCarbon(writeOff.writeoff_date).toFormat(
                "X"
            );

            this.$store.dispatch("checkBillingLock", {
                date,
                onProceed: () => this.$bvModal.show("modal-delete-writeoff"),
                onError: message => this._message(message, "danger")
            });
        }
    }
};
</script>
